"use client";

import { Fallback, Image, Root } from "@radix-ui/react-avatar";
import * as React from "react";
import AccountFilled20 from "@/icons/__generated__/AccountFilled/AccountFilled20";
import AccountFilled24 from "@/icons/__generated__/AccountFilled/AccountFilled24";
import AccountFilled28 from "@/icons/__generated__/AccountFilled/AccountFilled28";
import AccountFilled32 from "@/icons/__generated__/AccountFilled/AccountFilled32";
import AccountFilled48 from "@/icons/__generated__/AccountFilled/AccountFilled48";
import { cn } from "@/lib/utils";

export type AvatarSize = 24 | 32 | 40 | 48 | 64 | 96 | 120;

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: AvatarSize;
}

export interface AvatarImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  className?: string;
  alt?: string;
}

export interface AvatarFallbackProps
  extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  alt?: string;
}

const AvatarSizeContext = React.createContext<AvatarSize>(32);

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size = 32, ...props }, ref) => (
    <AvatarSizeContext.Provider value={size}>
      <Root
        ref={ref}
        className={cn(
          "tw-relative tw-flex tw-shrink-0 tw-overflow-hidden tw-rounded-circular",
          {
            "tw-size-24": size === 24,
            "tw-size-32": size === 32,
            "tw-size-40": size === 40,
            "tw-size-48": size === 48,
            "tw-size-64": size === 64,
            "tw-size-96": size === 96,
            "tw-size-[120px]": size === 120,
          },
          className,
        )}
        {...props}
      />
    </AvatarSizeContext.Provider>
  ),
);
Avatar.displayName = "Avatar";

const AvatarImage = React.forwardRef<HTMLImageElement, AvatarImageProps>(
  ({ className, ...props }, ref) => (
    <Image
      ref={ref}
      loading="lazy"
      className={cn("tw-aspect-square tw-size-full", className)}
      {...props}
    />
  ),
);
AvatarImage.displayName = "AvatarImage";

const getAvatarFallback = (size: AvatarSize) => {
  const ScaledAvatarFallbackIcon: Record<
    AvatarSize,
    React.ComponentType<React.SVGProps<SVGSVGElement>>
  > = {
    24: AccountFilled20,
    32: AccountFilled24,
    40: AccountFilled28,
    48: AccountFilled32,
    64: AccountFilled48,
    96: AccountFilled48,
    120: AccountFilled48,
  };

  // For sizes reusing a smaller icon, scale the SVG up via CSS so it fills the
  // avatar proportionally. The tw-size class overrides the icon's intrinsic
  // width/height attributes while the viewBox keeps it crisp.
  const scaledIconClassName: Partial<Record<AvatarSize, string>> = {
    96: "tw-size-56", // 56px
    120: "tw-size-72", // 72px
  };

  const AvatarFallbackIcon = size
    ? ScaledAvatarFallbackIcon[size]
    : AccountFilled32;

  return (
    <div className="tw-flex tw-justify-center tw-items-center tw-size-full tw-bg-gray-200 tw-rounded-circular">
      <AvatarFallbackIcon
        className={cn("tw-text-gray-500", size && scaledIconClassName[size])}
      />
    </div>
  );
};

const AvatarFallback = React.forwardRef<HTMLDivElement, AvatarFallbackProps>(
  ({ className, ...props }, ref) => {
    const size = React.useContext(AvatarSizeContext);

    return (
      <Fallback
        ref={ref}
        className={cn(
          "tw-flex tw-items-center tw-justify-center tw-size-full tw-rounded-circular",
          className,
        )}
        {...props}
      >
        {getAvatarFallback(size)}
      </Fallback>
    );
  },
);
AvatarFallback.displayName = "AvatarFallback";

export { Avatar, AvatarFallback, AvatarImage };
